"use client";

import React, { useMemo, useState } from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import {
  type Control,
  type ControllerRenderProps,
  useWatch,
} from "react-hook-form";

import type { OrgProfileModel } from "@/api";
import {
  DocumentationEditorFlyout,
  DocumentationInlinePanel,
} from "@/components/documentation-markdown-field";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { documentationKindFromWorkTypeName } from "@/lib/documentation/generate-documentation";
import { cn } from "@/lib/utils";
import { type WorkFormValues } from "@/schemas/work-form-schema";

// TextInputField component for text inputs
export const TextInputField = React.memo(function TextInputField({
  control,
  name,
  label,
  placeholder,
}: {
  control: Control<WorkFormValues>;
  name: keyof WorkFormValues;
  label: string;
  placeholder?: string;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        // Convert value to string if it's a Date, or use nullish coalescing for null/undefined
        const inputValue =
          field.value instanceof Date
            ? field.value.toISOString().split("T")[0] // Format date as YYYY-MM-DD
            : ((field.value as string) ?? "");

        return (
          <FormItem>
            <FormLabel>{label}</FormLabel>
            <FormControl>
              <Input
                placeholder={placeholder}
                name={field.name}
                ref={field.ref}
                onBlur={field.onBlur}
                onChange={field.onChange}
                value={inputValue}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
});

// DatePickerField component to reduce repetition
export function DatePickerField({
  control,
  name,
  label,
}: {
  control: Control<WorkFormValues>;
  name: keyof WorkFormValues;
  label: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex flex-col">
          <FormLabel>{label}</FormLabel>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <FormControl>
                <Button
                  variant={"outline"}
                  className={cn(
                    "w-full pl-3 text-left font-normal",
                    !field.value && "text-muted-foreground",
                  )}
                >
                  {field.value ? (
                    format(new Date(field.value as string), "PPP")
                  ) : (
                    <span>Pick a date</span>
                  )}
                  <CalendarIcon className="ml-auto size-4 opacity-50" />
                </Button>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                captionLayout="dropdown"
                selected={
                  field.value ? new Date(field.value as string) : undefined
                }
                onSelect={(date) => {
                  // Only update and close when a valid date is selected
                  if (date) {
                    field.onChange(date);
                    setOpen(false);
                  }
                }}
                fromYear={1900}
                toYear={2100}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

// SelectField component to reduce repetition
export function SelectField({
  control,
  name,
  label,
  placeholder,
  options,
  disabled,
}: {
  control: Control<WorkFormValues>;
  name: keyof WorkFormValues;
  label: string;
  placeholder: string;
  options: OrgProfileModel[] | { id: string | null; name: string }[];
  disabled?: boolean;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        // Debug logging
        return (
          <FormItem>
            <FormLabel>{label}</FormLabel>
            <Select
              onValueChange={field.onChange}
              value={
                field.value !== null && field.value !== undefined
                  ? String(field.value)
                  : ""
              }
              defaultValue={
                field.value !== null && field.value !== undefined
                  ? String(field.value)
                  : ""
              }
              disabled={disabled}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={placeholder} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {options.map((option, index) => (
                  <SelectItem
                    key={option.id || `empty-key-${index}`}
                    value={option.id || `no-value-${index}`} // Use a unique non-empty string instead of empty string
                  >
                    {option.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

// NumberField component to reduce repetition
export function NumberField({
  control,
  name,
  label,
  placeholder,
  formatAsCurrency = false,
  currencyCode = "USD",
}: {
  control: Control<WorkFormValues>;
  name: keyof WorkFormValues;
  label: string;
  placeholder: string;
  formatAsCurrency?: boolean;
  currencyCode?: string;
}) {
  const [isFocused, setIsFocused] = useState(false);

  // Create a wrapper component to handle the field rendering
  // This allows us to use hooks at the top level
  const FieldRenderer = ({
    field,
  }: {
    field: ControllerRenderProps<WorkFormValues, keyof WorkFormValues>;
  }) => {
    // Now useMemo is at the top level of this component
    const displayValue = useMemo(() => {
      if (
        !formatAsCurrency ||
        isFocused ||
        field.value === null ||
        field.value === undefined
      ) {
        return field.value === null || field.value === undefined
          ? ""
          : String(field.value);
      }
      // Convert field.value to number safely before formatting
      const numValue =
        typeof field.value === "number"
          ? field.value
          : typeof field.value === "string"
            ? parseFloat(field.value)
            : 0;
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currencyCode,
      }).format(numValue);
    }, [field.value]); // formatAsCurrency, isFocused, and currencyCode are stable references from parent component

    return (
      <FormItem>
        <FormLabel>{label}</FormLabel>
        <FormControl>
          <Input
            type={isFocused ? "number" : "text"}
            placeholder={placeholder}
            value={displayValue}
            onChange={(e) => {
              const value = e.target.value;
              const numericValue = value.replace(/[^0-9.-]+/g, "");
              field.onChange(numericValue === "" ? null : Number(numericValue));
            }}
            onFocus={() => {
              setIsFocused(true);
            }}
            onBlur={() => {
              setIsFocused(false);
            }}
            name={field.name}
            ref={field.ref}
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    );
  };

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => <FieldRenderer field={field} />}
    />
  );
}

// TextareaField component for description
export function TextareaField({
  control,
  name,
  label,
  placeholder,
}: {
  control: Control<WorkFormValues>;
  name: keyof WorkFormValues;
  label: string;
  placeholder: string;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Textarea
              placeholder={placeholder}
              className="resize-none"
              {...field}
              value={
                field.value === null || field.value === undefined
                  ? ""
                  : String(field.value)
              }
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export function DocumentationField({
  control,
  workId,
  workTypes,
  isProjectRoot = false,
  persistDocumentation,
  active = true,
  compact = false,
  variant = "flyout",
}: {
  control: Control<WorkFormValues>;
  workId?: string;
  workTypes?: OrgProfileModel[];
  isProjectRoot?: boolean;
  persistDocumentation?: (documentation: string) => Promise<void>;
  active?: boolean;
  compact?: boolean;
  /** `inline` renders the editor in-place (task detail two-column layout). */
  variant?: "flyout" | "inline";
}): JSX.Element {
  const workTypeId = useWatch({ control, name: "workTypeId" });
  const name = useWatch({ control, name: "name" });
  const description = useWatch({ control, name: "description" });
  const workTypeName =
    workTypes?.find((type) => type.id === workTypeId)?.name ?? null;
  const templateKind = isProjectRoot
    ? "Project"
    : documentationKindFromWorkTypeName(workTypeName);

  return (
    <FormField
      control={control}
      name="documentation"
      render={({ field }) => {
        const documentation =
          typeof field.value === "string" ? field.value : "";
        const generate = workId
          ? {
              targetKind: "work" as const,
              targetId: workId,
              templateKind,
              name: typeof name === "string" ? name : "",
              description:
                typeof description === "string" ? description : "",
            }
          : null;

        const editor =
          variant === "inline" ? (
            <DocumentationInlinePanel
              documentation={documentation}
              onChange={field.onChange}
              persistDocumentation={persistDocumentation}
              active={active}
              generate={generate}
            />
          ) : (
            <DocumentationEditorFlyout
              documentation={documentation}
              onChange={field.onChange}
              persistDocumentation={persistDocumentation}
              active={active}
              generate={generate}
            />
          );

        if (compact) {
          return editor;
        }
        return (
          <FormItem>
            {editor}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
